import { z } from "zod";
import {
  emailSchema,
  hostnameSchema,
  isMediaPath,
  longTextSchema,
  optionalText,
  phoneSchema,
  currencySchema,
  safeUrl,
  shortTextSchema,
  slugSchema,
  timezoneSchema,
} from "./primitives.js";

const hexColour = z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Use a hex colour");

/**
 * A province or territory.
 *
 * The thirteen Canadian subdivision codes rather than free text, because this is the field a
 * directory is browsed and filtered by, and "ON", "Ont." and "Ontario" as three values is a filter
 * that finds a third of what it should. Uppercased on the way in, so nobody is corrected for typing
 * lower case.
 */
export const CANADIAN_REGIONS = [
  "AB",
  "BC",
  "MB",
  "NB",
  "NL",
  "NS",
  "NT",
  "NU",
  "ON",
  "PE",
  "QC",
  "SK",
  "YT",
] as const;

export type CanadianRegion = (typeof CANADIAN_REGIONS)[number];

export const regionSchema = z
  .string()
  .trim()
  .toUpperCase()
  .pipe(z.enum(CANADIAN_REGIONS, { message: "Use a province or territory code, like ON" }));

/**
 * Where one branding image lives — an upload, or a link to one somewhere else.
 *
 * Both, not one or the other, and both in the same field. Uploading is what a community actually
 * wants: the logo is a PNG in somebody's email and the alternative was asking a volunteer to find
 * it a public URL first, which is a step most band offices cannot complete. But a link still has to
 * work, because some communities already host their artwork and the ones that do should not have to
 * re-upload it to keep it.
 *
 * The uploaded form is `/media/{ulid}` — see `isMediaPath` for why a path and not a URL. Storing
 * both kinds in one field is what keeps every reader unchanged: a logo is a string that goes in an
 * `img src` either way, and a second field would mean every surface asking which one to prefer.
 */
const imageUrl = z
  .string()
  .trim()
  .max(1000)
  .refine((value) => isMediaPath(value) || safeUrl(value) !== undefined, {
    message: "Upload an image, or paste a link starting with https://",
  });

/** A sponsor's logo line — the name is what renders when there is no image to show. */
export const sponsorLogoSchema = z.object({
  name: shortTextSchema,
  url: imageUrl.optional(),
});

/**
 * What the public leaderboard is allowed to say.
 *
 * Kept **inside** `brandingJson` rather than given a column of its own, because it is read on
 * exactly the same path as the colours — the public results page needs both in its first byte, and
 * a second JSON column would mean a second read on the hottest request in the system. Both
 * defaults are the conservative ones: a community publishes names and scores by deciding to, not
 * by forgetting to turn them off.
 */
export const leaderboardSchema = z.object({
  showParticipantNames: z.boolean().default(false),
  showScores: z.boolean().default(false),
  /** Whether the prize amount appears next to a placing, or only the placing itself. */
  showPrizeAmounts: z.boolean().default(false),
});

export type LeaderboardSettings = z.infer<typeof leaderboardSchema>;

/**
 * A community's branding. Every field is optional: the defaults in `@powwow/design-tokens` are
 * chosen to be legible in direct sunlight, and a community that supplies nothing gets those
 * rather than an unstyled page.
 */
export const brandingSchema = z.object({
  leaderboard: leaderboardSchema.optional(),
  primary: hexColour.optional(),
  primaryContrast: hexColour.optional(),
  secondary: hexColour.optional(),
  secondaryContrast: hexColour.optional(),
  /*
   * There were two attachment-id fields here, reserved for the day uploads existed. That day
   * arrived and they were the wrong shape: an id is not renderable, so every surface would have had
   * to ask "id or URL?" and resolve one of them, and the banner strip and the sponsor list would
   * each have needed a parallel id field of their own. One string that is already an `img src`
   * whichever way it was set is fewer moving parts in more places.
   */
  eventLogo: imageUrl.optional(),
  /**
   * The single banner, kept for the communities that already have one stored.
   *
   * Superseded by `heroImages` and never written by the settings screen any more. It is still read,
   * because dropping it would silently blank a banner somebody set — and a community that has one
   * of these and no list gets a strip of exactly one picture, which is the same thing it always was.
   */
  heroImage: imageUrl.optional(),
  /**
   * The pictures across the top of the community's public page, in the order they are shown.
   *
   * Capped at twelve. Every one of them is a full-width image on a phone on venue wifi, and the
   * strip is above everything a visitor actually came for — a community that pastes fifty URLs has
   * not made a better page, it has made a slower one.
   */
  heroImages: z.array(imageUrl).max(12).optional(),
  /** The browser-tab icon for the community's public pages. A square image reads best; absent
   *  falls back to the platform mark. */
  favicon: imageUrl.optional(),
  welcomeMessage: longTextSchema.optional(),
  footerText: longTextSchema.optional(),
  sponsorLogos: z.array(sponsorLogoSchema).max(50).optional(),
  contactInfo: longTextSchema.optional(),
});

export type Branding = z.infer<typeof brandingSchema>;

/**
 * Terminology overrides. Loosely typed on purpose — the key set lives in `@powwow/localization`
 * and unknown keys are dropped there rather than rejected here, so adding a term to the
 * vocabulary never invalidates a community's stored settings.
 */
export const terminologySchema = z.record(
  z.string(),
  z.union([
    z.string().trim().min(1).max(80),
    z.object({ one: z.string().optional(), other: z.string().optional() }),
  ]),
);

export const createCommunitySchema = z.object({
  slug: slugSchema,
  name: shortTextSchema,
  legalName: optionalText(shortTextSchema),
  /** Set during onboarding from what the community says, never guessed from an IP address. */
  timezone: timezoneSchema,
  defaultLocale: z.literal("en-CA").default("en-CA"),
  branding: brandingSchema.optional(),
  terminology: terminologySchema.optional(),
});

/**
 * What a platform admin supplies to onboard a community from the admin console.
 *
 * A superset of `createCommunitySchema`: the same community fields, plus the first owner, because a
 * community with no one who can sign in and run it is not yet onboarded. The owner is named by
 * email so the account can be found-or-created — one person may own more than one community. `slug`
 * becomes the hostname (`{slug}.pow-wow.ca`), which is why it is the DNS-safe `slugSchema` and not
 * free text.
 */
export const createCommunityAdminSchema = z.object({
  slug: slugSchema,
  name: shortTextSchema,
  legalName: optionalText(shortTextSchema),
  timezone: timezoneSchema,
  defaultLocale: z.literal("en-CA").default("en-CA"),
  currency: currencySchema.default("CAD"),
  region: regionSchema.optional(),
  ownerName: shortTextSchema,
  ownerEmail: emailSchema,
});

export type CreateCommunityAdminInput = z.infer<typeof createCommunityAdminSchema>;

export const updateCommunitySchema = createCommunitySchema.partial().omit({ slug: true });

export const addCommunityDomainSchema = z.object({
  hostname: hostnameSchema,
  isPrimary: z.boolean().default(false),
});

/**
 * The community profile a signed-in owner edits, as one screen's worth of fields.
 *
 * Nullable where a value can be removed — `website: null` clears it, an absent field is left
 * alone — matching the event patch convention. `archived` is deliberately not reachable from
 * here: retiring a community is a platform operation, not a settings toggle.
 */
export const updateCommunityProfileSchema = z.object({
  name: shortTextSchema.optional(),
  legalName: shortTextSchema.nullable().optional(),
  publicName: shortTextSchema.nullable().optional(),
  shortName: shortTextSchema.nullable().optional(),
  description: longTextSchema.nullable().optional(),
  contactEmail: emailSchema.nullable().optional(),
  contactPhone: phoneSchema.nullable().optional(),
  mailingAddress: longTextSchema.nullable().optional(),
  website: z.url().max(500).nullable().optional(),
  /** Province or territory. Named `region` to match `events.region` — see the schema's own note. */
  region: regionSchema.nullable().optional(),
  timezone: timezoneSchema.optional(),
  defaultLocale: z.string().min(2).max(20).optional(),
  supportedLocales: z.array(z.string().min(2).max(20)).max(10).nullable().optional(),
  currency: currencySchema.optional(),
  status: z.enum(["active", "suspended"]).optional(),
});

/**
 * What a platform admin may change about an existing community from the console.
 *
 * The same attribute fields the owner edits, minus the three that are not a platform admin's to
 * set: `status` (its own dedicated deactivate/archive actions, which also touch `archivedAt`),
 * and the locale pair (a community's own language settings, not a platform concern). `slug` is
 * absent because it is the hostname and immutable here. Reusing the owner schema keeps the two
 * edit surfaces from drifting field by field.
 */
export const updateCommunityAdminSchema = updateCommunityProfileSchema.omit({
  status: true,
  defaultLocale: true,
  supportedLocales: true,
});

export type UpdateCommunityAdminInput = z.infer<typeof updateCommunityAdminSchema>;

/** Transfer a community's ownership to a person named by email — found-or-created like onboarding. */
export const reassignCommunityOwnerSchema = z.object({
  ownerName: shortTextSchema,
  ownerEmail: emailSchema,
});

export type ReassignCommunityOwnerInput = z.infer<typeof reassignCommunityOwnerSchema>;

export const updateBrandingSchema = z.object({
  branding: brandingSchema,
});

export const updateTerminologySchema = z.object({
  terminology: terminologySchema,
});

export type CreateCommunityInput = z.infer<typeof createCommunitySchema>;
export type UpdateCommunityInput = z.infer<typeof updateCommunitySchema>;
export type UpdateCommunityProfileInput = z.infer<typeof updateCommunityProfileSchema>;
